// AppSelectionModal.jsx - Fixed SVG rendering with Most Used indicator
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Grid,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useUser } from "./UserContextPersistent";
import VehicleAppIcon from "../../images/appIcon/vehicleIcon.svg";
import SchoolAppIcon from "../../images/appIcon/schoolIcon.svg";
import StaffAppIcon from "../../images/appIcon/staffIcon.svg";

const AppSelectionModal = ({
  open,
  onClose,
  onAppSelect,
  organizationData,
}) => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [availableApps, setAvailableApps] = useState([]);
  const [currentAppId, setCurrentAppId] = useState(null);

  // Detect current app based on localStorage or window location
  const getCurrentAppId = () => {
    try {
      // First check if selectedApp is in localStorage
      const selectedApp = localStorage.getItem("selectedApp");
      if (selectedApp) {
        return selectedApp;
      }

      // Detect based on current URL path
      const pathname = window.location.pathname;
      if (pathname.startsWith("/school")) {
        return "schoolTrack";
      } else if (
        pathname.startsWith("/dashboard") ||
        pathname === "/overview"
      ) {
        return "vehicleTrack";
      }

      return null;
    } catch (e) {
      console.error("Error detecting current app:", e);
      return null;
    }
  };

  useEffect(() => {
    // Set current app ID when modal opens
    if (open) {
      const appId = getCurrentAppId();
      console.log("Current app detected:", appId);
      setCurrentAppId(appId);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    console.log("=== APP SELECTION MODAL DEBUG ===");
    console.log("1. organizationData prop:", organizationData);
    console.log("2. context state.organization:", state.organization);
    console.log("3. state.isAuthenticated:", state.isAuthenticated);
    console.log("4. currentAppId:", currentAppId);

    let orgData = organizationData || state.organization;

    if (!orgData) {
      try {
        const userState = JSON.parse(localStorage.getItem("userState"));
        orgData = userState?.organization;
        console.log("3. Organization from localStorage userState:", orgData);
      } catch (e) {
        console.error("Error parsing userState:", e);
      }
    }

    console.log("5. Final orgData:", orgData);
    console.log("6. organizationType:", orgData?.organizationType);
    console.log(
      "7. Is organizationType an array?:",
      Array.isArray(orgData?.organizationType),
    );

    if (!orgData?.organizationType) {
      console.log("No organizationType found - closing modal");
      setAvailableApps([]);
      onClose();
      return;
    }

    const apps = [];
    const orgTypes = Array.isArray(orgData.organizationType)
      ? orgData.organizationType
      : [orgData.organizationType];

    console.log("8. Processed orgTypes:", orgTypes);

    // If the org only has ONE app total, no switcher is needed
    if (orgTypes.length <= 1) {
      console.log("Only one app configured - closing modal");
      setAvailableApps([]);
      onClose();
      return;
    }

    if (orgTypes.includes("vehicleTrack") && currentAppId !== "vehicleTrack") {
      console.log("Adding vehicleTrack app");
      apps.push({
        id: "vehicleTrack",
        name: "Fleet Management System",
        description:
          "Monitor vehicles in real-time, plan routes, get anomaly alerts and analytical insights.",
        icon: VehicleAppIcon,
        route: "/dashboard",
        color: "#20A8E0",
      });
    }

    if (orgTypes.includes("schoolTrack") && currentAppId !== "schoolTrack") {
      console.log("Adding schoolTrack app");
      apps.push({
        id: "schoolTrack",
        name: "School Bus Tracking",
        description:
          "Ensure students' safety with real-time bus monitoring and alert systems.",
        icon: SchoolAppIcon,
        route: "/school",
        color: "#FF9800",
      });
    }

    if (orgTypes.includes("staffTrack") && currentAppId !== "staffTrack") {
      console.log("Adding staffTrack app");
      apps.push({
        id: "staffTrack",
        name: "Staff Tracking",
        description: "Monitor field staff movements in real time.",
        icon: StaffAppIcon,
        route: "https://staff.routeeye.io",
        color: "#4CAF50",
      });
    }

    // if (orgTypes.includes("custom") && currentAppId !== "custom") {
    //   console.log("Adding custom app");
    //   apps.push({
    //     id: "custom",
    //     name: "Custom Dashboard",
    //     description: "Access your custom workspace",
    //     icon: DashboardCustomizeIcon,
    //     route: "/dashboard",
    //     color: "#9C27B0",
    //   });
    // }

    console.log("9. Final Available Apps:", apps);

    if (apps.length === 0) {
      console.log("No apps configured - closing modal");
      setAvailableApps([]);
      onClose();
      return;
    }

    setAvailableApps(apps);
  }, [open, organizationData, state.organization, state.isAuthenticated, onClose, currentAppId]);

  const handleAppSelect = (app) => {
    // Store selected app in localStorage
    localStorage.setItem("selectedApp", app.id);
    console.log("App selected:", app.id, "Navigating to:", app.route);
    if (app.id !== "staffTrack") {
      localStorage.setItem("selectedApp", app.id);
    }
    // Call onAppSelect callback if provided, otherwise use navigate directly
    if (onAppSelect) {
      onAppSelect(app);
    } else {
      // Only navigate if it's an internal route
      if (app.route.startsWith("/")) {
        navigate(app.route);
      } else {
        // For external routes, open in new tab
        window.location.href = app.route;
      }
      onClose();
    }
  };

  const handleClose = (event, reason) => {
    if (
      availableApps.length > 0 &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
    onClose();
  };

  const displayOrgData = organizationData || state.organization;
  console.log("Display Organization Data:", displayOrgData);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={availableApps.length > 0}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: "16px",
          maxWidth: "600px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", paddingBottom: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              color: "#183059",
              fontFamily: "Roboto",
              fontWeight: 600,
              fontStyle: "SemiBold",
              fontSize: "28px",
              lineHeight: "100%",
              letterSpacing: "0%",
            }}
          >
            Welcome, {displayOrgData?.contactName || "User"}👋
          </Typography>
          {availableApps.length === 0 && (
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, fontSize: "20px", color: "#414958" }}
        >
          Select a module to continue.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {availableApps.length === 0 ? (
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ py: 4 }}
          >
            No applications available for your organization. Please contact your
            administrator to configure applications.
          </Typography>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {availableApps.map((app) => {
                return (
                  <Grid item xs={12} sm={6} key={app.id}>
                    {/* {app.id === "vehicleTrack" && (
                      <>
                        <Box
                          sx={{
                            position: "absolute",
                            height: "30px",
                            left: "20px",
                            top: "104px",
                            backgroundColor: "#2F80ED",
                            color: "#FFFFFF",
                            padding: "12px",
                            borderRadius: "12px 12px 4px 12px",
                            fontSize: "12px",
                            fontFamily: "Roboto",
                            zIndex: 1,
                            boxShadow: "0px 2px 8px rgba(47, 128, 237, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Most Used
                        </Box>
                        <Box
                          sx={{
                            position: "absolute",
                            height: "30px",
                            left: "28px",
                            top: "110px",
                            backgroundColor: "#2F80ED",
                            color: "#FFFFFF",
                            borderRadius: "0px 6px 0px 0px ",

                            padding: "4px 12px",
                            rotate: "-140deg",
                            fontSize: "12px",
                            fontWeight: 600,
                            fontFamily: "Roboto",
                            boxShadow: "0px 2px 8px rgba(47, 128, 237, 0.3)",
                          }}
                        />
                      </>
                    )} */}
                    <Card
                      onClick={() => handleAppSelect(app)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "#FFFFFF",
                        transition: "all 0.3s ease",
                        position: "relative",
                        boxShadow: "0px 8px 24px 0px #0B1F4440",
                        borderRadius: "12px",
                        height: "100%",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          border: "1px solid #2F80ED",
                        },
                      }}
                    >
                      {/* Most Used Badge - Only for vehicleTrack */}
                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          height: "100%",
                          gap: 2,
                        }}
                      >
                        <Box
                          className="app-icon-box"
                          sx={{
                            height: 64,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "transform 0.3s ease",
                          }}
                        >
                          <img
                            src={app.icon}
                            alt={app.name}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontStyle: "Medium",
                              fontSize: "20px",
                              lineHeight: "100%",
                              letterSpacing: "0%",
                              textTransform: "capitalize",
                              color: "#111214",
                            }}
                          >
                            {app.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontStyle: "Regular",
                              fontSize: "14px",
                            }}
                          >
                            {app.description}
                          </Typography>
                        </Box>
                        <Box
                          variant="contained"
                          size="small"
                          onClick={() => handleAppSelect(app)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            border: "1px solid #D6E4FF",
                            color: "#20A8E0",
                            fontFamily: "Roboto",
                            backgroundColor: "#FFFFFF",
                            fontWeight: 500,
                            fontSize: "14px",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            textTransform: "none",
                          }}
                        >
                          <Typography variant="body2">Launch</Typography>{" "}
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.0615 19.0615L17.1215 12.0005L10.0615 4.93945L7.93945 7.06145L12.8795 12.0005L7.93945 16.9395L10.0615 19.0615Z"
                              fill="#20A8E0"
                            />
                          </svg>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontWeight: 400,
                      fontStyle: "Regular",
                      fontSize: "16px",
                      textAlign: "center",
                      color: "#414958",
                    }}
                  >
                    You can switch modules anytime from the Profile actions.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AppSelectionModal;
